import React, { useState, useEffect } from "react";
import {
  EyeIcon,
  CheckCircleIcon,
  UserGroupIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import { adminApi } from "../../repositories/admin";
import Pagination from "../../components/common/Pagination";
import DataTable, { DataTableColumn, dataTableRowClass, dataTableThClass, dataTableTdClass } from "../../components/ui/DataTable";
import { Skeleton, SkeletonTableRows } from "../../components/ui/skeleton";
import { ArrowUp, ArrowDown, ChevronDown } from "lucide-react";
import { cn } from "../../DashboardPage/lib/utils";

interface UserEngagement {
  fullName: string;
  company: string;
  viewedAt: string;
  dismissed: string | null;
  isDismissed: boolean;
}

interface AnalyticsData {
  totalPublishedUpdates: number;
  totalAppUpdatesViewed: number;
  totalAppUpdatesDismissed: number;
  totalUserNeverSeen: number;
  totalUsers: number;
  uniqueUsersWhoViewed: number;
  totalAppUpdatesWithViews: number;
  averageViewRate: number;
  averageDismissalRate: number;
  userAppUpdates: UserEngagement[];
}

interface Pagination {
  page: number;
  limit: number;
  totalItems: number;
  totalPages: number;
}

const Engagements: React.FC = () => {
  const [sortField, setSortField] = useState<"fullName" | "viewedAt" | "isDismissed">("viewedAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [dismissalFilter, setDismissalFilter] = useState<"all" | "dismissed" | "pending">("all");
  const [loading, setLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 0,
  });

  useEffect(() => {
    fetchAnalytics();
  }, [pagination.page, pagination.limit, dismissalFilter, sortField, sortDirection]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getAppUpdateAnalytics({
        page: pagination.page,
        limit: pagination.limit,
        filter: dismissalFilter,
        sort: sortField,
        sortDirection,
      });
      if (response?.data?.success) {
        setAnalyticsData(response.data.data);
        if (response.data.data.pagination) {
          setPagination((prev) => ({
            ...prev,
            ...response.data.data.pagination,
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const totalAnnouncementsPublished = analyticsData?.totalPublishedUpdates || 0;
  const totalUsersViewed = analyticsData?.totalAppUpdatesViewed || 0;
  const totalUsersDismissed = analyticsData?.totalAppUpdatesDismissed || 0;
  const totalUsersNotLoggedIn = analyticsData?.totalUserNeverSeen || 0;
  const userEngagements = analyticsData?.userAppUpdates || [];
  const averageViewRate = analyticsData?.averageViewRate?.toFixed(1) || "0.0";
  const averageDismissalRate = analyticsData?.averageDismissalRate?.toFixed(1) || "0.0";

  const handleSort = (field: "fullName" | "viewedAt" | "isDismissed") => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (filter: "all" | "dismissed" | "pending") => {
    setDismissalFilter(filter);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleLimitChange = (limit: number) => {
    setPagination((prev) => ({ ...prev, limit, page: 1 }));
  };

  if (loading && !analyticsData) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="p-4 bg-white border rounded-lg border-neutral-200">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <Skeleton className="w-28 h-3" />
                  <Skeleton className="w-16 h-6" />
                </div>
                <Skeleton className="w-10 h-10 rounded-full" />
              </div>
            </div>
          ))}
        </div>
        <div className="overflow-hidden bg-white border rounded-lg border-neutral-200">
          <table className="min-w-full">
            <tbody>
              <SkeletonTableRows rows={8} columns={4} />
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Header markup comes from DataTable's `head` prop (sortable headers and
  // multi-line cells); this list only sets the column count for its
  // loading and empty state rows.
  const engagementColumns: DataTableColumn<any>[] = [
    { key: "user", header: "" },
    { key: "company", header: "" },
    { key: "viewed", header: "" },
    { key: "dismissed", header: "" },
  ];
  return (
    <div>
      {/* Overall Stats Section */}
      <div className="grid grid-cols-1 gap-4 mb-6 md:grid-cols-3">
        <div className="p-4 bg-white border rounded-lg border-neutral-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-neutral-500 sm:text-sm">Published Updates</p>
              <p className="mt-1 text-xl font-bold text-black sm:text-2xl">{totalAnnouncementsPublished}</p>
            </div>
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-primary-50 text-primary-700">
              <ChartBarIcon className="w-5 h-5" />
            </span>
          </div>
        </div>
        <div className="p-4 bg-white border rounded-lg border-neutral-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-neutral-500 sm:text-sm">Average View Rate</p>
              <p className="mt-1 text-xl font-bold text-black sm:text-2xl">{averageViewRate}%</p>
            </div>
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-primary-50 text-primary-700">
              <EyeIcon className="w-5 h-5" />
            </span>
          </div>
        </div>
        <div className="p-4 bg-white border rounded-lg border-neutral-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-neutral-500 sm:text-sm">Average Dismissal Rate</p>
              <p className="mt-1 text-xl font-bold text-black sm:text-2xl">{averageDismissalRate}%</p>
            </div>
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-primary-50 text-primary-700">
              <CheckCircleIcon className="w-5 h-5" />
            </span>
          </div>
        </div>
      </div>

      {/* Per Announcement Stats */}
      <div className="grid grid-cols-1 gap-4 mb-6 md:grid-cols-3">
        <div className="p-4 bg-white border rounded-lg border-neutral-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-neutral-500 sm:text-sm">Viewed by users</p>
              <p className="mt-1 text-xl font-bold text-black sm:text-2xl">{totalUsersViewed}</p>
            </div>
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-primary-50 text-primary-700">
              <EyeIcon className="w-5 h-5" />
            </span>
          </div>
        </div>
        <div className="p-4 bg-white border rounded-lg border-neutral-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-neutral-500 sm:text-sm">Dismissed by users</p>
              <p className="mt-1 text-xl font-bold text-black sm:text-2xl">{totalUsersDismissed}</p>
            </div>
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-primary-50 text-primary-700">
              <CheckCircleIcon className="w-5 h-5" />
            </span>
          </div>
        </div>
        <div className="p-4 bg-white border rounded-lg border-neutral-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-neutral-500 sm:text-sm">Unseen By Users</p>
              <p className="mt-1 text-xl font-bold text-black sm:text-2xl">{totalUsersNotLoggedIn}</p>
            </div>
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-primary-50 text-primary-700">
              <UserGroupIcon className="w-5 h-5" />
            </span>
          </div>
        </div>
      </div>

      {/* Detailed View - User Engagement Table */}
      <div className="mb-6">
        <div className="flex flex-col items-start justify-between gap-4 mb-4 sm:flex-row sm:items-center">
          <div className="flex gap-2">
            <button
              onClick={() => handleFilterChange("all")}
              className={`px-3 py-1.5 text-xs sm:text-sm rounded-md transition-colors ${
                dismissalFilter === "all"
                  ? "bg-primary-700 text-white"
                  : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
              }`}
            >
              All
            </button>
            <button
              onClick={() => handleFilterChange("dismissed")}
              className={`px-3 py-1.5 text-xs sm:text-sm rounded-md transition-colors ${
                dismissalFilter === "dismissed"
                  ? "bg-primary-700 text-white"
                  : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
              }`}
            >
              Dismissed
            </button>
            <button
              onClick={() => handleFilterChange("pending")}
              className={`px-3 py-1.5 text-xs sm:text-sm rounded-md transition-colors ${
                dismissalFilter === "pending"
                  ? "bg-primary-700 text-white"
                  : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
              }`}
            >
              Pending
            </button>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-neutral-700">Items per page:</label>
            <div className="relative">
              <select
                value={pagination.limit}
                onChange={(e) => handleLimitChange(Number(e.target.value))}
                className="py-2 pl-3 pr-9 text-sm text-black transition-colors bg-white border rounded-md cursor-pointer appearance-none border-neutral-300 hover:border-primary-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <ChevronDown className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none right-3 top-1/2 text-neutral-500" />
            </div>
          </div>
        </div>

        <DataTable
          columns={engagementColumns}
          loading={loading}
          skeletonRows={8}
          head={
            <thead>
            <tr className="border-b border-neutral-200 bg-primary-50">
            <th
            className={cn(dataTableThClass, "cursor-pointer transition-colors hover:bg-primary-100")}
            onClick={() => handleSort("fullName")}
            >
            <div className="flex items-center gap-2">
            User Name
            {sortField === "fullName" && (sortDirection === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />)}
            </div>
            </th>
            <th className={dataTableThClass}><div className="flex items-center gap-2">Company</div></th>
            <th
            className={cn(dataTableThClass, "cursor-pointer transition-colors hover:bg-primary-100")}
            onClick={() => handleSort("viewedAt")}
            >
            <div className="flex items-center gap-2">
            Viewed At
            {sortField === "viewedAt" && (sortDirection === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />)}
            </div>
            </th>
            <th
            className={cn(dataTableThClass, "cursor-pointer transition-colors hover:bg-primary-100")}
            onClick={() => handleSort("isDismissed")}
            >
            <div className="flex items-center gap-2">
            Dismissed
            {sortField === "isDismissed" && (sortDirection === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />)}
            </div>
            </th>
            </tr>
            </thead>
          }
        >
              {userEngagements.length > 0 ? (
                userEngagements.map((engagement, index) => (
                  <tr key={index} className={dataTableRowClass}>
                    <td className={cn(dataTableTdClass, "font-medium")}>{engagement.fullName}</td>
                    <td className={cn(dataTableTdClass, "text-neutral-600")}>{engagement.company}</td>
                    <td className={cn(dataTableTdClass, "text-neutral-600")}>
                      {new Date(engagement.viewedAt).toLocaleString('en-US', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className={dataTableTdClass}>
                      {engagement.isDismissed ? (
                        <div className="flex flex-col">
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-medium border rounded-full bg-success-50 text-success-800 border-success-200 w-fit">
                            <CheckCircleIcon className="w-3 h-3" />
                            Yes
                          </span>
                          {engagement.dismissed && (
                            <span className="mt-1 text-xs text-neutral-500">
                              {new Date(engagement.dismissed).toLocaleString('en-US', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold text-warning-800 bg-warning-50 border border-warning-200 rounded-full">
                          Pending
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="px-4 py-10 text-sm text-center text-neutral-500" colSpan={4}>
                    No engagement data available for the selected filter.
                  </td>
                </tr>
              )}
        </DataTable>

        {/* Shared Pagination, same as the other three tabs on this page. */}
        {pagination.totalPages > 1 && (
          <div className="mt-4">
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              setPage={handlePageChange}
              totalItems={pagination.totalItems}
              limit={pagination.limit}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Engagements;
